import { MUICheckBox, MUIDateTimePicker, MUISelect } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import dayjs, { Dayjs } from "dayjs"
import { StatusCodes } from "http-status-codes"
import { MouseEvent, useCallback, useEffect, useState } from "react"
import { Controller, ControllerRenderProps, useForm, useWatch } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { useApplicationRoles } from "../../../../../hooks/useApplicationRoles"
import { useApplications } from "../../../../../hooks/useApplications"
import { digitalManagerApi } from "../../../../../services/api"
import { FieldRow, FormPage } from "../../../../components/FormPage"
import { useApplicationSelectRenderer } from "../../../../components/Helpers"
import { ApplicationRoleGet } from "../../../../models/ApplicationRole"
import {
  ApplicationRoleUserGet,
  ApplicationRoleUserPost,
} from "../../../../models/ApplicationRoleUser"
import { AccessLevel, DimaFeatureShortNameEnum } from "../../../../models/Authorization"
import { Guid } from "../../../../models/CustomType"
import { ControllerRendererProps } from "../../../../types/reactHookForm"
import { getNowDateTime } from "../../../../utils/getFormattedDate"

const validationSchema = z.object({
  applicationId: z.string().min(1, "Application is required"),
  roleId: z.string().min(1, "Role is required"),
  validityStartDate: z
    .string()
    .min(1, "Validity Start Date is required")
    .refine((val) => !Number.isNaN(Date.parse(val)), "Invalid date format"),
  validityEndDate: z
    .string()
    .min(1, "Validity End Date is required")
    .refine((val) => !Number.isNaN(Date.parse(val)), "Invalid date format"),
  isDisabled: z.boolean({ error: "Is Disabled is required" }),
})

const MAX_DATE = dayjs("9999-12-31T23:59:59")

export function UserApplicationsRoleCreate() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const returnURL = `/users/${userId}#apps-roles`
  const { applicationsList, isLoadingApplications } = useApplications(
    DimaFeatureShortNameEnum.RM,
    AccessLevel.Write
  )
  const [userRolesIds, setUserRolesIds] = useState<Guid[]>([])

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors, isSubmitting, isDirty, isValid },
  } = useForm<{ applicationId: string; roleId: string } & ApplicationRoleUserPost>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  const applicationId = useWatch({
    control,
    name: "applicationId",
  })

  useEffect(() => {
    reset({
      applicationId: "",
      roleId: "",
      validityStartDate: new Date(getNowDateTime()).toISOString(),
      validityEndDate: "",
      isDisabled: false,
    })
  }, [reset])

  const onSubmit = useCallback(
    (values: { applicationId: string; roleId: string } & ApplicationRoleUserPost) => digitalManagerApi
        .post(
          `/users/${userId}/applications/${values.applicationId}/roles/${values.roleId}`,
          {
            validityStartDate: values.validityStartDate,
            validityEndDate: values.validityEndDate,
            isDisabled: values.isDisabled,
          }
        )
        .then((res) => {
          if (res.status === StatusCodes.CREATED) {
            toast.success("Role added to user")
            navigate(returnURL)
          }
        })
        .catch(() => {
          toast.error("Cannot add role to user")
        }),
    [userId, navigate, returnURL]
  )

  useEffect(() => {
    if (applicationId) {
      digitalManagerApi
        .get(`/users/${userId}/roles`, {
          params: { applicationId },
        })
        .then((res) => {
          if (res.status === StatusCodes.OK && res.data?.length) {
            setUserRolesIds(
              res.data.map((userRole: ApplicationRoleUserGet) => userRole.role.id)
            )
          }
        })
    }
  }, [applicationId, userId])

  const { applicationRoles, isLoadingApplicationRoles } =
    useApplicationRoles(applicationId)

  const handleAutocompleteGetOptionDisabled = useCallback(
    (option: ApplicationRoleGet) => userRolesIds.includes(option.id),
    [userRolesIds]
  )

  const handleAutocompleteGetOptionLabel = useCallback(
    (option: ApplicationRoleGet) => option.name,
    []
  )
  const handleApplicationChange = useCallback(
    (value: string) => {
      setValue("applicationId", value, { shouldValidate: true })
      setValue("roleId", "", { shouldValidate: true })
    },
    [setValue]
  )

  const submitForm = handleSubmit(onSubmit)

  const handleFieldAndApplicationChange = useCallback(
    (
        field: ControllerRenderProps<
          { applicationId: string; roleId: string } & ApplicationRoleUserPost,
          "applicationId"
        >
      ) =>
      (value: string) => {
        field.onChange(value)
        handleApplicationChange(value)
      },
    [handleApplicationChange]
  )

  const handleSelectApplicationIdChange = useCallback(
    (
        field: ControllerRenderProps<
          { applicationId: string; roleId: string } & ApplicationRoleUserPost,
          "applicationId"
        >
      ) =>
      (values: Array<string | { id: string }>) => {
        const first = Array.isArray(values) && values.length > 0 ? values[0] : ""
        const value = typeof first === "string" ? first : first?.id ?? ""
        handleFieldAndApplicationChange(field)(value)
      },
    [handleFieldAndApplicationChange]
  )

  const renderSelectApplicationId = useApplicationSelectRenderer({
    applicationsList,
    errors,
    isLoadingApplications,
    handleApplicationSelectChange: handleSelectApplicationIdChange,
  })

  const handleAutocompleteSelectChange = useCallback(
    (
        field: ControllerRenderProps<
          { applicationId: string; roleId: string } & ApplicationRoleUserPost,
          "roleId"
        >
      ) =>
      (next: Array<string | { id: string }>) => {
        const firstItem = next?.[0]
        const selectedId = typeof firstItem === "string" ? firstItem : firstItem?.id
        field.onChange(selectedId ?? "")
      },
    []
  )

  const renderAutocompleteRoleId = useCallback(
    ({
      field,
    }: ControllerRendererProps<
      { applicationId: string; roleId: string } & ApplicationRoleUserPost,
      "roleId"
    >) => (
      <MUISelect
        id="roleId"
        name="roleId"
        placeholder="Select role"
        size={32}
        dropdownLOV={(applicationRoles ?? []).map((role) => ({
          id: role.id,
          name: handleAutocompleteGetOptionLabel(role) ?? "",
          isDisabled: handleAutocompleteGetOptionDisabled(role),
        }))}
        enableSearch
        clearable={false}
        isDisabled={isLoadingApplicationRoles}
        selectedItem={field.value ? [field.value] : []}
        onChange={handleAutocompleteSelectChange(field)}
        errors={
          typeof errors.roleId?.message === "string"
            ? { roleId: { message: errors.roleId.message } }
            : undefined
        }
      />
    ),
    [
      applicationRoles,
      isLoadingApplicationRoles,
      handleAutocompleteGetOptionLabel,
      handleAutocompleteGetOptionDisabled,
      handleAutocompleteSelectChange,
      errors.roleId?.message,
    ]
  )

  const renderRoleIdController = useCallback(
    () => (
      <Controller name="roleId" control={control} render={renderAutocompleteRoleId} />
    ),
    [control, renderAutocompleteRoleId]
  )

  const handleDateChangeValidityStartDate = useCallback(
    (
        field: ControllerRenderProps<
          { applicationId: string; roleId: string } & ApplicationRoleUserPost,
          "validityStartDate"
        >
      ) =>
      (date: Dayjs | null) => {
        field.onChange(date ? date.toISOString() : "")
        const validityEndDate = getValues("validityEndDate")
        if (validityEndDate && date?.isAfter(validityEndDate)) {
          setValue("validityEndDate", "")
        }
      },
    [getValues, setValue]
  )

  const handleValidityStartDatePickerChange = useCallback(
    (
        field: ControllerRenderProps<
          { applicationId: string; roleId: string } & ApplicationRoleUserPost,
          "validityStartDate"
        >
      ) =>
      (date: Dayjs | null) => {
        if (date?.isAfter(MAX_DATE)) {
          setValue("validityStartDate", MAX_DATE.toISOString(), {
            shouldValidate: true,
          })
        } else {
          handleDateChangeValidityStartDate(field)(date)
        }
      },
    [handleDateChangeValidityStartDate, setValue]
  )

  const renderDateTimePickerValidityStartDate = useCallback(
    ({
      field,
    }: ControllerRendererProps<
      { applicationId: string; roleId: string } & ApplicationRoleUserPost,
      "validityStartDate"
    >) => (
      <MUIDateTimePicker
        id="validityStartDate"
        name="validityStartDate"
        size={40}
        value={field.value ? dayjs(field.value) : null}
        isDisabled={false}
        onChange={handleValidityStartDatePickerChange(field)}
        errors={
          errors.validityStartDate
            ? { validityStartDate: { message: String(errors.validityStartDate.message) } }
            : undefined
        }
      />
    ),
    [errors.validityStartDate, handleValidityStartDatePickerChange]
  )

  const handleDateChangeValidityEndDate = useCallback(
    (
        field: ControllerRenderProps<
          { applicationId: string; roleId: string } & ApplicationRoleUserPost,
          "validityEndDate"
        >
      ) =>
      (date: Dayjs | null) => {
        field.onChange(date ? date.toISOString() : "")
      },
    []
  )

  const handleValidityEndDatePickerChange = useCallback(
    (
        field: ControllerRenderProps<
          { applicationId: string; roleId: string } & ApplicationRoleUserPost,
          "validityEndDate"
        >
      ) =>
      (date: Dayjs | null) => {
        const minDate = getValues("validityStartDate")
          ? dayjs(getValues("validityStartDate"))
          : null

        if (date?.isBefore(minDate as Dayjs)) {
          setValue("validityEndDate", "", { shouldValidate: true })
          return
        }

        if (date?.isAfter(MAX_DATE)) {
          setValue("validityEndDate", MAX_DATE.toISOString(), { shouldValidate: true })
          return
        }

        handleDateChangeValidityEndDate(field)(date)
      },
    [getValues, handleDateChangeValidityEndDate, setValue]
  )

  const renderDateTimePickerValidityEndDate = useCallback(
    ({
      field,
    }: ControllerRendererProps<
      { applicationId: string; roleId: string } & ApplicationRoleUserPost,
      "validityEndDate"
    >) => (
      <MUIDateTimePicker
        id="validityEndDate"
        name="validityEndDate"
        size={40}
        value={field.value ? dayjs(field.value) : null}
        isDisabled={false}
        onChange={handleValidityEndDatePickerChange(field)}
        errors={
          errors.validityEndDate
            ? { validityEndDate: { message: String(errors.validityEndDate.message) } }
            : undefined
        }
      />
    ),
    [errors.validityEndDate, handleValidityEndDatePickerChange]
  )

  const handleCheckboxToggle = useCallback(
    (
        field: ControllerRenderProps<
          { applicationId: string; roleId: string } & ApplicationRoleUserPost,
          "isDisabled"
        >
      ) =>
      (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation()
        field.onChange(!field.value)
      },
    []
  )

  const renderCheckboxIsDisabled = useCallback(
    ({
      field,
    }: ControllerRendererProps<
      { applicationId: string; roleId: string } & ApplicationRoleUserPost,
      "isDisabled"
    >) => (
      <MUICheckBox
        id="isDisabled"
        checked={!!field.value}
        handleToggle={handleCheckboxToggle(field)}
      />
    ),
    [handleCheckboxToggle]
  )

  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={submitForm}
      isSubmitting={isSubmitting}
      isDirty={isDirty}
      isValid={isValid}
    >
      <FieldRow label="Application">
        <Controller
          name="applicationId"
          control={control}
          render={renderSelectApplicationId}
        />
      </FieldRow>
      <FieldRow label="Role">
        <Controller name="roleId" control={control} render={renderRoleIdController} />
      </FieldRow>
      <FieldRow label="Validity Start Date">
        <Controller
          name="validityStartDate"
          control={control}
          render={renderDateTimePickerValidityStartDate}
        />
      </FieldRow>
      <FieldRow label="Validity End Date">
        <Controller
          name="validityEndDate"
          control={control}
          render={renderDateTimePickerValidityEndDate}
        />
      </FieldRow>
      <FieldRow label="Is Disabled">
        <Controller
          name="isDisabled"
          control={control}
          render={renderCheckboxIsDisabled}
        />
      </FieldRow>
    </FormPage>
  )
}
